import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaEuroSign,
  FaFileInvoice,
  FaFileSignature,
  FaGift,
  FaHandshake,
  FaPaperPlane,
  FaPercent,
  FaRegClock,
  FaSyncAlt,
  FaUsers,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Button from "../../../../components/Button/Button";
import {
  addParrainage,
  getSubscriptionsByAssociation,
} from "../../../../services/abonnementParrainageServices";

const ASSO_ID =   process.env.REACT_APP_ASSO_ID || 102;

const EMPTY_PARRAINAGE_FORM = {
  nom_parrain: "",
  prenom_parrain: "",
  club_parrain: "",
  email_parrain: "",
  telephone_parrain: "",
  club_parraine: "",
  nom_contact_parraine: "",
  prenom_contact_parraine: "",
  email_parraine: "",
  telephone_parraine: "",
};

const normalizeKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const parseDateSafe = (value) => {
  if (!value) return null;

  const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T00:00:00`)
    : new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

const dayAnchor = (date) => {
  const copy = new Date(date);
  copy.setHours(12, 0, 0, 0);
  return copy;
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const formatDate = (value) => {
  const date = parseDateSafe(value);
  if (!date) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const moneyToNumber = (value) => {
  if (typeof value === "number") return value;
  if (value === null || value === undefined) return NaN;

  let str = String(value).trim();
  if (!str) return NaN;

  str = str.replace(/\s/g, "").replace(/€|eur/gi, "");
  str = str.replace(/[^0-9,.\-]/g, "");

  if (str.includes(",") && str.includes(".")) {
    str = str.replace(/\./g, "").replace(",", ".");
  } else if (str.includes(",") && !str.includes(".")) {
    str = str.replace(",", ".");
  }

  const number = Number(str);
  return Number.isFinite(number) ? number : NaN;
};

const formatEuro = (value) => {
  const number = moneyToNumber(value);
  if (!Number.isFinite(number)) return "—";

  return number.toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const isQuotePending = (subscription) => {
  const key = normalizeKey(subscription?.status);

  return (
    key.includes("devis") ||
    key.includes("signature") ||
    key.includes("quote")
  );
};

const statusBadge = (status) => {
  const raw = String(status || "").trim();
  const key = normalizeKey(raw);

  if (!key) {
    return {
      label: "—",
      className:
        "border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-300",
    };
  }

  if (
    key.includes("devis") ||
    key.includes("signature") ||
    key.includes("quote")
  ) {
    return {
      label: "Devis à signer",
      className:
        "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900/60 dark:bg-violet-950/40 dark:text-violet-200",
    };
  }

  if (
    key === "active" ||
    key === "abonne" ||
    key.includes("abonn") ||
    key === "subscribed"
  ) {
    return {
      label: "Abonné",
      className:
        "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200",
    };
  }

  if (key === "pending" || key.includes("attente")) {
    return {
      label: "En attente",
      className:
        "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200",
    };
  }

  if (
    key === "expired" ||
    key.includes("expire") ||
    key.includes("termine") ||
    key.includes("annule") ||
    key === "cancelled" ||
    key === "canceled"
  ) {
    return {
      label: "Terminé",
      className:
        "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-200",
    };
  }

  if (key.includes("pause")) {
    return {
      label: "En pause",
      className:
        "border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-900/60 dark:bg-purple-950/40 dark:text-purple-200",
    };
  }

  if (key.includes("progress") || key.includes("cours")) {
    return {
      label: "En cours",
      className:
        "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-200",
    };
  }

  return {
    label: raw || "—",
    className:
      "border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-300",
  };
};

const periodicityBadge = (periodicity) => {
  const key = normalizeKey(periodicity);

  if (
    key === "yearly" ||
    key === "annual" ||
    key === "annuel" ||
    key.includes("ann")
  ) {
    return {
      label: "Annuel",
      className:
        "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900/60 dark:bg-sky-950/40 dark:text-sky-200",
    };
  }

  if (key === "monthly" || key === "mensuel" || key.includes("mens")) {
    return {
      label: "Mensuel",
      className:
        "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-900/60 dark:bg-indigo-950/40 dark:text-indigo-200",
    };
  }

  if (
    key === "trimestrial" ||
    key === "quarterly" ||
    key.includes("trimes")
  ) {
    return {
      label: "Trimestriel",
      className:
        "border-cyan-200 bg-cyan-50 text-cyan-700 dark:border-cyan-900/60 dark:bg-cyan-950/40 dark:text-cyan-200",
    };
  }

  return {
    label: periodicity || "—",
    className:
      "border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-300",
  };
};

const computeProgress = (startDate, endDate) => {
  const start = parseDateSafe(startDate);
  const end = parseDateSafe(endDate);

  if (!start || !end) {
    return {
      valid: false,
      percent: 0,
      daysRemaining: null,
      barClassName: "bg-slate-400",
      text: "—",
    };
  }

  const today = dayAnchor(new Date());
  const startAnchor = dayAnchor(start);
  const endAnchor = dayAnchor(end);

  const totalMs = endAnchor.getTime() - startAnchor.getTime();
  const daysRemaining = Math.ceil(
    (endAnchor.getTime() - today.getTime()) / 86400000
  );

  let percent = 0;

  if (totalMs <= 0) {
    percent = today.getTime() >= endAnchor.getTime() ? 100 : 0;
  } else if (today.getTime() <= startAnchor.getTime()) {
    percent = 0;
  } else if (today.getTime() >= endAnchor.getTime()) {
    percent = 100;
  } else {
    percent = ((today.getTime() - startAnchor.getTime()) / totalMs) * 100;
  }

  percent = clamp(Math.round(percent), 0, 100);

  let barClassName = "bg-emerald-500";

  if (today.getTime() > endAnchor.getTime()) {
    barClassName = "bg-rose-500";
  } else if (daysRemaining <= 30) {
    barClassName = "bg-amber-500";
  }

  let text = `J-${Math.max(0, daysRemaining)} • ${percent}%`;

  if (today.getTime() > endAnchor.getTime()) {
    text = `Expiré • ${percent}%`;
  } else if (today.getTime() < startAnchor.getTime()) {
    text = `Démarre bientôt • ${percent}%`;
  }

  return {
    valid: true,
    percent,
    daysRemaining,
    barClassName,
    text,
  };
};

function Badge({ children, className = "" }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold ${className}`}
    >
      {children}
    </span>
  );
}

function ProgressGauge({ progress }) {
  if (!progress?.valid) {
    return (
      <span className="text-sm italic text-gray-500 dark:text-gray-400">
        —
      </span>
    );
  }

  return (
    <div className="min-w-[170px]">
      <div className="mb-1 flex items-center justify-between gap-3 text-[11px] text-gray-600 dark:text-gray-300">
        <span className="font-bold">{progress.text}</span>
        <span className="tabular-nums">{progress.percent}%</span>
      </div>

      <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-white/10">
        <div
          className={`h-full rounded-full transition-[width] duration-500 ${progress.barClassName}`}
          style={{ width: `${progress.percent}%` }}
        />
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, hint }) {
  return (
    <div className="rounded-3xl border border-black/10 bg-white/80 p-4 shadow-sm backdrop-blur dark:border-white/10 dark:bg-white/5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {label}
          </p>
          <p className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">
            {value}
          </p>
          {hint ? (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {hint}
            </p>
          ) : null}
        </div>

        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 text-white shadow-sm">
          <Icon />
        </div>
      </div>
    </div>
  );
}

function EmptyState({ title, text }) {
  return (
    <div className="rounded-3xl border border-dashed border-black/10 bg-white/50 p-8 text-center dark:border-white/10 dark:bg-white/5">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 text-white">
        <FaRegClock />
      </div>
      <h3 className="mt-4 text-base font-bold text-gray-900 dark:text-white">
        {title}
      </h3>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{text}</p>
    </div>
  );
}

export default function AbonnementParrainage() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingParrainage, setSendingParrainage] = useState(false);
  const [showParrainageForm, setShowParrainageForm] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [formData, setFormData] = useState(EMPTY_PARRAINAGE_FORM);

  const ui = useMemo(() => {
    const card =
      "rounded-3xl border border-black/10 bg-white/80 shadow-sm backdrop-blur " +
      "dark:bg-white/5 dark:border-white/10";
    const title = "text-gray-900 dark:text-white";
    const muted = "text-gray-600 dark:text-gray-300";
    const chip =
      "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold " +
      "border border-black/10 dark:border-white/10 bg-white/70 dark:bg-black/20";
    const input =
      "w-full rounded-2xl border border-black/10 bg-white/80 px-3 py-2.5 text-sm " +
      "text-gray-900 outline-none transition placeholder:text-gray-400 " +
      "focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20 " +
      "dark:border-white/10 dark:bg-black/20 dark:text-white dark:placeholder:text-gray-500";
    return { card, title, muted, chip, input };
  }, []);

  const checkUserRole = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user && user.role_id === 4) setIsDemoMode(true);
  };

  const guardDemo = () => {
    if (isDemoMode) {
      toast.info("Fonction désactivée en mode Démonstration");
      return true;
    }

    return false;
  };

  const fetchSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getSubscriptionsByAssociation(ASSO_ID);
      setSubscriptions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur récupération abonnements :", error);
      toast.error("Erreur lors de la récupération des abonnements.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptions();
    checkUserRole();
  }, [fetchSubscriptions]);

  const isParrainage = useCallback((subscription) => {
    const amount = moneyToNumber(subscription?.total_amount);
    return Number.isFinite(amount) && amount < 0;
  }, []);

  const { abonnements, devis, parrainages } = useMemo(() => {
    const result = {
      abonnements: [],
      devis: [],
      parrainages: [],
    };

    for (const subscription of subscriptions) {
      if (isParrainage(subscription)) {
        result.parrainages.push(subscription);
      } else if (isQuotePending(subscription)) {
        result.devis.push(subscription);
      } else {
        result.abonnements.push(subscription);
      }
    }

    return result;
  }, [subscriptions, isParrainage]);

  const stats = useMemo(() => {
    const totalParrainages = parrainages.reduce((sum, item) => {
      const amount = moneyToNumber(item?.total_amount);
      return Number.isFinite(amount) ? sum + Math.abs(amount) : sum;
    }, 0);

    const activeSubscription = abonnements.find((item) => {
      const key = normalizeKey(item?.status);
      return key.includes("active") || key.includes("abonn");
    });

    const nextEndDate = activeSubscription?.end_date
      ? formatDate(activeSubscription.end_date)
      : "—";

    return {
      abonnements: abonnements.length,
      devis: devis.length,
      parrainages: parrainages.length,
      totalParrainages,
      nextEndDate,
    };
  }, [abonnements, devis, parrainages]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData(EMPTY_PARRAINAGE_FORM);
  };

  const handleSubmitParrainage = async (event) => {
    event.preventDefault();

    if (guardDemo()) return;

    try {
      setSendingParrainage(true);

      await addParrainage(formData);

      toast.success("Parrainage envoyé avec succès ✅");
      resetForm();
    } catch (error) {
      console.error("Erreur envoi parrainage :", error);
      toast.error("Erreur lors de l'envoi du parrainage.");
    } finally {
      setSendingParrainage(false);
    }
  };

  const renderDesktopSubscriptionTable = (items, type) => {
    const isDevis = type === "devis";
    const isParrainageTable = type === "parrainages";

    return (
      <div className="hidden lg:block">
        <div className={`${ui.card} overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-black/5 dark:bg-white/5">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                    {isParrainageTable ? "Type" : "Produit"}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                    Montant
                  </th>
                  {!isParrainageTable && (
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                      Périodicité
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                    Début
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                    Fin
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                    Statut
                  </th>
                  {!isDevis && !isParrainageTable && (
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                      Progression
                    </th>
                  )}
                  {!isParrainageTable && (
                    <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                      Document
                    </th>
                  )}
                </tr>
              </thead>

              <tbody className="divide-y divide-black/5 dark:divide-white/10">
                {items.map((item) => {
                  const status = statusBadge(item.status);
                  const periodicity = periodicityBadge(item.periodicity);
                  const progress = computeProgress(item.start_date, item.end_date);
                  const documentLink = isDevis
                    ? item.drive_quote_link
                    : item.drive_invoice_link;

                  return (
                    <tr
                      key={`${type}-${item.id}`}
                      className="hover:bg-black/5 dark:hover:bg-white/5"
                    >
                      <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white">
                        <div className="flex items-center gap-2">
                          {isParrainageTable && (
                            <Badge className="border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900/60 dark:bg-violet-950/40 dark:text-violet-200">
                              Parrainage
                            </Badge>
                          )}
                          <span>{item.product || "—"}</span>
                        </div>
                      </td>

                      <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white">
                        {formatEuro(item.total_amount)}
                      </td>

                      {!isParrainageTable && (
                        <td className="px-4 py-3">
                          <Badge className={periodicity.className}>
                            {periodicity.label}
                          </Badge>
                        </td>
                      )}

                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                        {formatDate(item.start_date)}
                      </td>

                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                        {formatDate(item.end_date)}
                      </td>

                      <td className="px-4 py-3">
                        <Badge className={status.className}>{status.label}</Badge>
                      </td>

                      {!isDevis && !isParrainageTable && (
                        <td className="px-4 py-3">
                          <ProgressGauge progress={progress} />
                        </td>
                      )}

                      {!isParrainageTable && (
                        <td className="px-4 py-3 text-center">
                          {documentLink ? (
                            <a
                              href={documentLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-black/10 bg-white/70 px-3 py-2 text-xs font-bold text-gray-800 transition hover:bg-white dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
                            >
                              {isDevis ? <FaFileSignature /> : <FaFileInvoice />}
                              {isDevis ? "Signer" : "Facture"} ↗
                            </a>
                          ) : (
                            <span className="text-sm italic text-gray-500 dark:text-gray-400">
                              Non disponible
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderMobileSubscriptionCards = (items, type) => {
    const isDevis = type === "devis";
    const isParrainageCard = type === "parrainages";

    return (
      <div className="space-y-4 lg:hidden">
        {items.map((item) => {
          const status = statusBadge(item.status);
          const periodicity = periodicityBadge(item.periodicity);
          const progress = computeProgress(item.start_date, item.end_date);
          const documentLink = isDevis
            ? item.drive_quote_link
            : item.drive_invoice_link;

          return (
            <div key={`mobile-${type}-${item.id}`} className={`${ui.card} p-5`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className={`text-base font-bold ${ui.title}`}>
                    {isParrainageCard ? "Parrainage" : item.product || "—"}
                  </div>

                  {isParrainageCard && item.product ? (
                    <p className={`mt-1 text-sm ${ui.muted}`}>{item.product}</p>
                  ) : null}
                </div>

                <Badge className={status.className}>{status.label}</Badge>
              </div>

              <div className="mt-4 space-y-2 text-sm text-gray-700 dark:text-gray-200">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-gray-500 dark:text-gray-400">Montant</span>
                  <span className="font-bold">{formatEuro(item.total_amount)}</span>
                </div>

                {!isParrainageCard && (
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-gray-500 dark:text-gray-400">
                      Périodicité
                    </span>
                    <Badge className={periodicity.className}>
                      {periodicity.label}
                    </Badge>
                  </div>
                )}

                <div className="flex items-center justify-between gap-3">
                  <span className="text-gray-500 dark:text-gray-400">Début</span>
                  <span>{formatDate(item.start_date)}</span>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <span className="text-gray-500 dark:text-gray-400">Fin</span>
                  <span>{formatDate(item.end_date)}</span>
                </div>
              </div>

              {!isDevis && !isParrainageCard && (
                <div className="mt-4">
                  <ProgressGauge progress={progress} />
                </div>
              )}

              {!isParrainageCard && (
                <div className="mt-4">
                  {documentLink ? (
                    <a
                      href={documentLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-black/10 bg-white/70 px-3 py-2 text-xs font-bold text-gray-800 transition hover:bg-white dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
                    >
                      {isDevis ? <FaFileSignature /> : <FaFileInvoice />}
                      {isDevis ? "Signer le devis" : "Voir la facture"} ↗
                    </a>
                  ) : (
                    <span className="text-sm italic text-gray-500 dark:text-gray-400">
                      Document non disponible
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen">
      <div className="mx-auto max-w-[1280px] px-4 py-8 sm:px-6 lg:px-8">
        <div className={`${ui.card} overflow-hidden`}>
          <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500 p-5 text-white sm:p-6">
            <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute -bottom-16 left-1/3 h-40 w-40 rounded-full bg-white/10 blur-2xl" />

            <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-bold backdrop-blur">
                  <FaHandshake />
                  Abonnement & Parrainage
                </div>

                <h1 className="mt-3 text-2xl font-bold sm:text-3xl">
                  Gestion de l’abonnement
                </h1>

                <p className="mt-1 max-w-3xl text-sm text-white/85">
                  Suivez vos abonnements, devis, factures et réductions liées au
                  parrainage directement depuis votre tableau de bord.
                </p>
              </div>

              <Button
                text={
                  <>
                    <FaSyncAlt className="mr-2" />
                    Actualiser
                  </>
                }
                onClick={fetchSubscriptions}
                className="!rounded-2xl !bg-white !text-slate-900 hover:!bg-white/90"
              />
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            icon={FaCheckCircle}
            label="Abonnements"
            value={stats.abonnements}
            hint="Contrats actifs ou historiques"
          />
          <StatCard
            icon={FaFileSignature}
            label="Devis"
            value={stats.devis}
            hint="À consulter ou signer"
          />
          <StatCard
            icon={FaGift}
            label="Parrainages"
            value={stats.parrainages}
            hint={`${formatEuro(stats.totalParrainages)} de réduction`}
          />
          <StatCard
            icon={FaCalendarAlt}
            label="Prochaine échéance"
            value={stats.nextEndDate}
            hint="Selon l'abonnement actif"
          />
        </div>

        {loading && (
          <div className="mt-6 space-y-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className={`${ui.card} h-24 animate-pulse bg-white/60 dark:bg-white/5`}
              />
            ))}
          </div>
        )}

        {!loading && !subscriptions.length && (
          <div className="mt-6">
            <EmptyState
              title="Aucun abonnement trouvé"
              text="Aucune donnée d’abonnement n’est encore rattachée à cette association."
            />
          </div>
        )}

        {!loading && devis.length > 0 && (
          <section className="mt-8">
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className={`text-xl font-bold ${ui.title}`}>
                  Devis à signer
                </h2>
                <p className={`mt-1 text-sm ${ui.muted}`}>
                  Les devis en attente de validation ou de renouvellement.
                </p>
              </div>
              <span className={ui.chip}>{devis.length} devis</span>
            </div>

            {renderDesktopSubscriptionTable(devis, "devis")}
            {renderMobileSubscriptionCards(devis, "devis")}
          </section>
        )}

        {!loading && abonnements.length > 0 && (
          <section className="mt-8">
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className={`text-xl font-bold ${ui.title}`}>
                  Abonnements
                </h2>
                <p className={`mt-1 text-sm ${ui.muted}`}>
                  Historique, factures, dates et progression de votre période en
                  cours.
                </p>
              </div>
              <span className={ui.chip}>{abonnements.length} élément(s)</span>
            </div>

            {renderDesktopSubscriptionTable(abonnements, "abonnements")}
            {renderMobileSubscriptionCards(abonnements, "abonnements")}
          </section>
        )}

        {!loading && parrainages.length > 0 && (
          <section className="mt-8">
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className={`text-xl font-bold ${ui.title}`}>
                  Réductions de parrainage
                </h2>
                <p className={`mt-1 text-sm ${ui.muted}`}>
                  Les montants négatifs correspondent aux remises obtenues grâce
                  aux clubs parrainés.
                </p>
              </div>
              <span className={ui.chip}>{parrainages.length} réduction(s)</span>
            </div>

            {renderDesktopSubscriptionTable(parrainages, "parrainages")}
            {renderMobileSubscriptionCards(parrainages, "parrainages")}
          </section>
        )}

        <section className="mt-8">
          <div className={`${ui.card} overflow-hidden`}>
            <div className="bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500 px-5 py-8 text-white sm:px-8">
              <div className="mx-auto max-w-4xl text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white/15 text-2xl backdrop-blur">
                  <FaUsers />
                </div>

                <h2 className="mt-4 text-xl font-bold sm:text-2xl">
                  Parrainez un club et profitez d’avantages
                </h2>

                <p className="mt-3 text-sm leading-6 text-white/90">
                  5% de réduction pour vous et le club parrainé. Jusqu’à{" "}
                  <span className="font-bold">10%</span> si vous parrainez
                  plusieurs clubs.
                </p>

                <div className="mt-5 flex flex-wrap items-center justify-center gap-2 text-xs">
                  <span className="rounded-full bg-white/90 px-3 py-1 font-bold text-slate-900">
                    Sans engagement
                  </span>
                  <span className="rounded-full bg-white/90 px-3 py-1 font-bold text-slate-900">
                    Réponse sous 24h
                  </span>
                  <span className="rounded-full bg-white/90 px-3 py-1 font-bold text-slate-900">
                    Avantages cumulables
                  </span>
                </div>

                <div className="mt-5">
                  <Button
                    text={
                      showParrainageForm
                        ? "Masquer le formulaire"
                        : "Parrainer un club"
                    }
                    onClick={() => setShowParrainageForm((prev) => !prev)}
                    className="!rounded-2xl !bg-white !text-slate-900 hover:!bg-white/90"
                  />
                </div>
              </div>
            </div>

            <div className="grid gap-6 p-5 sm:p-6 lg:grid-cols-5">
              <div className="order-2 lg:order-1 lg:col-span-2">
                <div className="h-full rounded-3xl border border-black/10 bg-white/70 p-5 dark:border-white/10 dark:bg-black/20">
                  <h3 className={`text-lg font-bold ${ui.title}`}>
                    Pourquoi parrainer ?
                  </h3>

                  <div className="mt-4 space-y-3">
                    <div className="flex gap-3">
                      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300">
                        <FaPercent />
                      </div>
                      <div>
                        <p className={`text-sm font-bold ${ui.title}`}>
                          Une remise immédiate
                        </p>
                        <p className={`text-sm ${ui.muted}`}>
                          Vous bénéficiez d’un avantage sur votre abonnement.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-300">
                        <FaHandshake />
                      </div>
                      <div>
                        <p className={`text-sm font-bold ${ui.title}`}>
                          Un club accompagné
                        </p>
                        <p className={`text-sm ${ui.muted}`}>
                          Le club parrainé découvre l’application dans les
                          meilleures conditions.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-violet-50 text-violet-600 dark:bg-violet-950/40 dark:text-violet-300">
                        <FaEuroSign />
                      </div>
                      <div>
                        <p className={`text-sm font-bold ${ui.title}`}>
                          Des avantages cumulables
                        </p>
                        <p className={`text-sm ${ui.muted}`}>
                          Plus vous recommandez l’application, plus la remise
                          peut devenir intéressante.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 rounded-3xl border border-violet-200 bg-violet-50 p-4 text-sm text-violet-800 dark:border-violet-900/60 dark:bg-violet-950/30 dark:text-violet-200">
                    <p className="font-bold">Astuce</p>
                    <p className="mt-1">
                      Indiquez au minimum le nom du club parrainé et un moyen de
                      contact pour faciliter la prise de contact.
                    </p>
                  </div>
                </div>
              </div>

              {showParrainageForm && (
                <div className="order-1 lg:order-2 lg:col-span-3">
                  <form
                    onSubmit={handleSubmitParrainage}
                    className="rounded-3xl border border-black/10 bg-white/70 p-5 dark:border-white/10 dark:bg-black/20"
                  >
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <h3 className={`text-lg font-bold ${ui.title}`}>
                          Proposer un club intéressé
                        </h3>
                        <p className={`mt-1 text-sm ${ui.muted}`}>
                          Complétez les informations du parrain et du club à
                          contacter.
                        </p>
                      </div>

                      <Badge className="border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900/60 dark:bg-violet-950/40 dark:text-violet-200">
                        Parrainage
                      </Badge>
                    </div>

                    <div className="mt-5 grid gap-5 md:grid-cols-2">
                      <div>
                        <p className={`mb-3 text-sm font-bold ${ui.title}`}>
                          Vos informations
                        </p>

                        <div className="space-y-3">
                          <input
                            name="club_parrain"
                            value={formData.club_parrain}
                            onChange={handleInputChange}
                            required
                            placeholder="Club parrain *"
                            className={ui.input}
                          />

                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <input
                              name="nom_parrain"
                              value={formData.nom_parrain}
                              onChange={handleInputChange}
                              required
                              placeholder="Nom *"
                              className={ui.input}
                            />

                            <input
                              name="prenom_parrain"
                              value={formData.prenom_parrain}
                              onChange={handleInputChange}
                              required
                              placeholder="Prénom *"
                              className={ui.input}
                            />
                          </div>

                          <input
                            name="email_parrain"
                            type="email"
                            value={formData.email_parrain}
                            onChange={handleInputChange}
                            required
                            placeholder="Email *"
                            className={ui.input}
                          />

                          <input
                            name="telephone_parrain"
                            type="tel"
                            inputMode="tel"
                            value={formData.telephone_parrain}
                            onChange={handleInputChange}
                            placeholder="Téléphone"
                            className={ui.input}
                          />
                        </div>
                      </div>

                      <div>
                        <p className={`mb-3 text-sm font-bold ${ui.title}`}>
                          Club parrainé
                        </p>

                        <div className="space-y-3">
                          <input
                            name="club_parraine"
                            value={formData.club_parraine}
                            onChange={handleInputChange}
                            required
                            placeholder="Club parrainé *"
                            className={ui.input}
                          />

                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <input
                              name="nom_contact_parraine"
                              value={formData.nom_contact_parraine}
                              onChange={handleInputChange}
                              placeholder="Nom du contact"
                              className={ui.input}
                            />

                            <input
                              name="prenom_contact_parraine"
                              value={formData.prenom_contact_parraine}
                              onChange={handleInputChange}
                              placeholder="Prénom du contact"
                              className={ui.input}
                            />
                          </div>

                          <input
                            name="email_parraine"
                            type="email"
                            value={formData.email_parraine}
                            onChange={handleInputChange}
                            placeholder="Email"
                            className={ui.input}
                          />

                          <input
                            name="telephone_parraine"
                            type="tel"
                            inputMode="tel"
                            value={formData.telephone_parraine}
                            onChange={handleInputChange}
                            placeholder="Téléphone"
                            className={ui.input}
                          />
                        </div>
                      </div>
                    </div>

                    <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                      Les champs marqués d’un * sont obligatoires. Les
                      informations sont utilisées uniquement pour traiter la
                      demande de parrainage.
                    </p>

                    <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                      <button
                        type="button"
                        onClick={resetForm}
                        disabled={sendingParrainage}
                        className="rounded-2xl border border-black/10 bg-white/70 px-4 py-2 text-sm font-bold text-gray-700 transition hover:bg-white disabled:opacity-60 dark:border-white/10 dark:bg-black/20 dark:text-gray-200 dark:hover:bg-black/30"
                      >
                        Réinitialiser
                      </button>

                      <Button
                        type="submit"
                        text={
                          <>
                            <FaPaperPlane className="mr-2" />
                            {sendingParrainage
                              ? "Envoi en cours..."
                              : "Envoyer le parrainage"}
                          </>
                        }
                        disabled={sendingParrainage}
                        className={`!rounded-2xl ${
                          sendingParrainage
                            ? "opacity-60 pointer-events-none"
                            : ""
                        }`}
                      />
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </section>

        <ToastContainer position="bottom-right" />
      </div>
    </div>
  );
}